/**
 * SkinLibrary.js
 * Bibliothek fuer eigene und geteilte Skins.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getAuthToken } from '../services/api';
import { useToastContext } from '../App';

/**
 * Laedt ein Skin-Bild mit Auth und gibt eine ObjectURL zurueck.
 * Fuer <img> / <canvas> Vorschau, da <img src> keinen Auth-Header senden kann.
 */
async function fetchSkinPreview(url) {
  const token = getAuthToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const resp = await fetch(url, { headers });
  if (!resp.ok) return null;
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

function VanillaSkinCard({ name, label, model, onUseAsTemplate }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 64;
    ctx.clearRect(0, 0, 64, 64);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = '#2a2a3a';
      ctx.fillRect(0, 0, 64, 64);
      ctx.drawImage(img, 8, 8, 8, 8, 8, 8, 48, 48);
      ctx.drawImage(img, 40, 8, 8, 8, 8, 8, 48, 48);
    };
    img.onerror = () => {
      ctx.fillStyle = '#2a2a3a';
      ctx.fillRect(0, 0, 64, 64);
      ctx.fillStyle = '#aaa';
      ctx.font = '10px monospace';
      ctx.fillText('Vanilla', 14, 36);
    };
    img.src = api.getDefaultSkinImageUrl(name);
  }, [name]);

  return (
    <div className="skin-card">
      <div className="skin-card-preview">
        <canvas
          ref={canvasRef}
          style={{ width: 64, height: 64, imageRendering: 'pixelated', borderRadius: 4 }}
        />
      </div>
      <div className="skin-card-info">
        <div className="skin-card-name">{label}</div>
        <div className="skin-card-meta">
          <span className="tag">{model === 'slim' ? 'Alex (slim)' : 'Steve (classic)'}</span>
          <span className="tag" style={{ background: 'rgba(0, 150, 255, 0.2)', color: '#4af', borderColor: '#4af' }}>
            Vanilla
          </span>
        </div>
      </div>
      <div className="skin-card-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => onUseAsTemplate(name, model)}
          title="Als Vorlage verwenden"
        >
          Als Vorlage
        </button>
      </div>
    </div>
  );
}

function SkinCard({ skin, isOwn, onDelete, onShare, onClone, onDownload }) {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const canvasRef = useRef(null);

  // Skin als 2D-Vorschau zeichnen (Kopf-Ausschnitt des Skins)
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 64;
    ctx.clearRect(0, 0, 64, 64);

    let cancelled = false;

    (async () => {
      try {
        const objUrl = await fetchSkinPreview(api.getSkinImageUrl(skin.id) + '?t=' + Date.now());
        if (cancelled || !objUrl) {
          ctx.fillStyle = '#2a2a3a';
          ctx.fillRect(0, 0, 64, 64);
          ctx.fillStyle = '#555';
          ctx.font = '10px monospace';
          ctx.fillText('?', 28, 36);
          return;
        }
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          ctx.imageSmoothingEnabled = false;
          ctx.fillStyle = '#2a2a3a';
          ctx.fillRect(0, 0, 64, 64);
          ctx.drawImage(img, 8, 8, 8, 8, 8, 8, 48, 48);
          ctx.drawImage(img, 40, 8, 8, 8, 8, 8, 48, 48);
          setImgLoaded(true);
          URL.revokeObjectURL(objUrl);
        };
        img.onerror = () => {
          ctx.fillStyle = '#2a2a3a';
          ctx.fillRect(0, 0, 64, 64);
          ctx.fillStyle = '#555';
          ctx.font = '10px monospace';
          ctx.fillText('?', 28, 36);
          URL.revokeObjectURL(objUrl);
        };
        img.src = objUrl;
      } catch {
        ctx.fillStyle = '#2a2a3a';
        ctx.fillRect(0, 0, 64, 64);
        ctx.fillStyle = '#555';
        ctx.font = '10px monospace';
        ctx.fillText('?', 28, 36);
      }
    })();

    return () => { cancelled = true; };
  }, [skin.id]);

  return (
    <div className="skin-card">
      <div className="skin-card-preview">
        <canvas
          ref={canvasRef}
          style={{ width: 64, height: 64, imageRendering: 'pixelated', borderRadius: 4 }}
        />
      </div>
      <div className="skin-card-info">
        <div className="skin-card-name">{skin.name}</div>
        <div className="skin-card-meta">
          <span className="tag">{skin.model === 'slim' ? 'Alex (slim)' : 'Steve (classic)'}</span>
          {skin.isShared && <span className="tag tag-shared">Geteilt</span>}
        </div>
        {skin.description && (
          <div className="skin-card-desc text-sm text-muted">{skin.description}</div>
        )}
        <div className="skin-card-date text-sm text-muted">
          {new Date(skin.updatedAt).toLocaleDateString('de-DE')}
        </div>
      </div>
      <div className="skin-card-actions">
        {isOwn && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`/skins/editor/${skin.id}`)}
            title="Skin bearbeiten"
          >
            Bearbeiten
          </button>
        )}
        {!isOwn && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onClone(skin)}
            title="In eigene Bibliothek kopieren"
          >
            Kopieren
          </button>
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => onDownload(skin)}
          title="PNG herunterladen"
        >
          Download
        </button>
        {isOwn && (
          <button
            className={`btn btn-sm ${skin.isShared ? 'btn-warning' : 'btn-secondary'}`}
            onClick={() => onShare(skin)}
            title={skin.isShared ? 'Nicht mehr teilen' : 'Mit anderen teilen'}
          >
            {skin.isShared ? 'Privat' : 'Teilen'}
          </button>
        )}
        {isOwn && (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => onDelete(skin)}
            title="Loeschen"
          >
            Loeschen
          </button>
        )}
      </div>
    </div>
  );
}

function SkinLibrary() {
  const navigate = useNavigate();
  const addToast = useToastContext();
  const [ownSkins, setOwnSkins] = useState([]);
  const [sharedSkins, setSharedSkins] = useState([]);
  const [vanillaSkins, setVanillaSkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vanilla');
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadSkins();
  }, []);

  async function loadSkins() {
    try {
      const data = await api.getSkins();
      setOwnSkins(data.own || []);
      setSharedSkins(data.shared || []);
      
      const vanilla = await api.getDefaultSkins();
      setVanillaSkins(vanilla || []);
    } catch (err) {
      addToast('Fehler beim Laden der Skins: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleUseVanillaAsTemplate(name, model) {
    try {
      const imgUrl = api.getDefaultSkinImageUrl(name);
      navigate('/skins/editor', { state: { templateUrl: imgUrl, model } });
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  async function handleDelete(skin) {
    if (deleteConfirm?.id === skin.id) {
      try {
        await api.deleteSkin(skin.id);
        setOwnSkins(prev => prev.filter(s => s.id !== skin.id));
        addToast('Skin gelöscht', 'success');
        setDeleteConfirm(null);
      } catch (err) {
        addToast('Fehler: ' + err.message, 'error');
      }
    } else {
      setDeleteConfirm(skin);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  }

  async function handleShare(skin) {
    try {
      await api.setSkinShared(skin.id, !skin.isShared);
      setOwnSkins(prev => prev.map(s => s.id === skin.id ? { ...s, isShared: !s.isShared } : s));
      addToast(skin.isShared ? 'Skin ist jetzt privat' : 'Skin wird geteilt', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  async function handleClone(skin) {
    try {
      const cloned = await api.cloneSkin(skin.id);
      setOwnSkins(prev => [cloned, ...prev]);
      setActiveTab('own');
      addToast('Skin in deine Bibliothek kopiert!', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  async function handleDownload(skin) {
    try {
      const objUrl = await fetchSkinPreview(api.getSkinImageUrl(skin.id));
      if (!objUrl) {
        addToast('Download fehlgeschlagen', 'error');
        return;
      }
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${skin.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (err) {
      addToast('Download fehlgeschlagen: ' + err.message, 'error');
    }
  }

  const filteredOwn = ownSkins.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.description?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredShared = sharedSkins.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="page-content"><div className="empty-state"><div className="spinner" /></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Skin-Bibliothek</h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            className="mc-input"
            placeholder="Skins durchsuchen..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          <button className="btn btn-primary" onClick={() => navigate('/skins/editor')}>
            + Neuer Skin
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <button
          className={`tab-btn ${activeTab === 'vanilla' ? 'active' : ''}`}
          onClick={() => setActiveTab('vanilla')}
        >
          Vanilla Templates ({vanillaSkins.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'own' ? 'active' : ''}`}
          onClick={() => setActiveTab('own')}
        >
          Meine Skins ({ownSkins.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'shared' ? 'active' : ''}`}
          onClick={() => setActiveTab('shared')}
        >
          Geteilte Skins ({sharedSkins.length})
        </button>
      </div>

      {/* Vanilla Skins */}
      {activeTab === 'vanilla' && (
        <div>
          {vanillaSkins.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🎮</div>
              <p>Vanilla-Skins konnten nicht geladen werden</p>
            </div>
          ) : (
            <div className="skin-library-grid">
              {vanillaSkins.map(skin => (
                <VanillaSkinCard
                  key={skin.name}
                  name={skin.name}
                  label={skin.label}
                  model={skin.model}
                  onUseAsTemplate={handleUseVanillaAsTemplate}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Eigene Skins */}
      {activeTab === 'own' && (
        <div>
          {filteredOwn.length === 0 ? (
            <div className="empty-state">
              <div className="icon">👤</div>
              {search ? (
                <p>Keine Skins gefunden fuer "{search}"</p>
              ) : (
                <>
                  <p>Noch keine Skins erstellt</p>
                  <button className="btn btn-primary" onClick={() => navigate('/skins/editor')}>
                    Ersten Skin erstellen
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="skin-library-grid">
              {filteredOwn.map(skin => (
                <SkinCard
                  key={skin.id}
                  skin={skin}
                  isOwn={true}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onClone={handleClone}
                  onDownload={handleDownload}
                  deleteConfirm={deleteConfirm}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Geteilte Skins */}
      {activeTab === 'shared' && (
        <div>
          {filteredShared.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🔗</div>
              {search ? (
                <p>Keine geteilten Skins gefunden fuer "{search}"</p>
              ) : (
                <p>Noch keine Skins von anderen Nutzern geteilt</p>
              )}
            </div>
          ) : (
            <div className="skin-library-grid">
              {filteredShared.map(skin => (
                <SkinCard
                  key={skin.id}
                  skin={skin}
                  isOwn={false}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onClone={handleClone}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loeschen-Bestaetigung */}
      {deleteConfirm && (
        <div className="toast-container">
          <div className="toast warning">
            Nochmal klicken um "{deleteConfirm.name}" zu loeschen
          </div>
        </div>
      )}
    </div>
  );
}

export default SkinLibrary;
